/* Indexing notifications, top right, on every page.

   Indexing is minutes of work on another machine and people move on meanwhile, so the
   result has to find them wherever they went. Only a change seen happening is announced:
   a failure from yesterday is not news on every page load.

   What was seen is kept for the tab, not the page: installing on GitHub, or a reload,
   is a full page load, and a repository that finished during it is still news. */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "@/api";
import { indexErrorText } from "@/onboarding/prototype";
import { useSession } from "@/session";
import { panel } from "@/ui";

const ACTIVE_POLL_MS = 4000;
const IDLE_POLL_MS = 30000;
const SUCCESS_MS = 10000;
const IN_FLIGHT = new Set(["not_indexed", "indexing"]);
const FINISHED = new Set(["indexed", "failed"]);
const SEEN_KEY = "codenerve.indexing-seen:";
const REQUESTED_EVENT = "codenerve:index-requested";

/* Another screen queued indexing (a Retry outside these notices). Without this, a retry
   that fails again between two polls looks like no change at all. */
export function announceIndexRequested(repositoryId) {
  window.dispatchEvent(new CustomEvent(REQUESTED_EVENT, { detail: { repositoryId } }));
}

/* Storage can be disabled; the notices then only remember for the life of the page. */
function readSeen(orgId) {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY + orgId);
    return raw ? new Map(Object.entries(JSON.parse(raw))) : null;
  } catch {
    return null;
  }
}

function writeSeen(orgId, seen) {
  try {
    sessionStorage.setItem(SEEN_KEY + orgId, JSON.stringify(Object.fromEntries(seen)));
  } catch {
    /* remembered for this page only */
  }
}

export function IndexingNotices() {
  const { status, orgId, organization, refresh } = useSession();
  const [notices, setNotices] = useState([]);
  /* Each repository's status at the last poll. Null until the first poll this tab has
     made for the organization, which keeps the existing state of the world from being
     announced as news. */
  const seen = useRef(null);
  const pollNow = useRef(() => {});
  const canRetry = organization?.role === "org_admin";

  useEffect(() => {
    seen.current = null;
    setNotices([]);
    if (status !== "in" || !orgId) return undefined;
    seen.current = readSeen(orgId);
    let cancelled = false;
    let running = false;
    let rerun = false;
    let timer = null;

    async function poll() {
      /* One loop only: a request to poll while one is in flight runs straight after it. */
      if (running) {
        rerun = true;
        return;
      }
      running = true;
      clearTimeout(timer);
      let active = false;
      try {
        const page = await api.get(`/organizations/${orgId}/repositories?limit=100`);
        if (cancelled) return;
        const items = (page?.items ?? []).filter((repo) => repo.enabled);
        const before = seen.current;
        if (before) {
          /* A repository missing from the last poll counts too: one adopted from GitHub
             can be added and indexed between two idle polls, and is first seen finished. */
          const finished = items.filter(
            (repo) => before.get(repo.id) !== repo.indexStatus && FINISHED.has(repo.indexStatus),
          );
          if (finished.length) {
            setNotices((current) => [
              ...current.filter((notice) => !finished.some((repo) => repo.id === notice.repo.id)),
              ...finished.map((repo) => ({ id: `${repo.id}:${repo.updatedAt}`, repo })),
            ]);
          }
        }
        seen.current = new Map(items.map((repo) => [repo.id, repo.indexStatus]));
        writeSeen(orgId, seen.current);
        active = items.some((repo) => IN_FLIGHT.has(repo.indexStatus));
      } catch (error) {
        /* A failed poll is retried on the next tick. A notification is never worth an
           error of its own. A 404 means this tab's organization list is out of date
           (membership ended since the page loaded): reloading it moves the session to an
           organization the account belongs to, instead of polling a refusal forever. */
        if (error?.status === 404) refresh();
      } finally {
        running = false;
      }
      if (cancelled) return;
      if (rerun) {
        rerun = false;
        timer = setTimeout(poll, 0);
      } else {
        timer = setTimeout(poll, active ? ACTIVE_POLL_MS : IDLE_POLL_MS);
      }
    }

    function requested(event) {
      const id = event.detail?.repositoryId;
      if (seen.current && id) seen.current.set(id, "not_indexed");
      setNotices((current) => current.filter((notice) => notice.repo.id !== id));
      poll();
    }

    pollNow.current = poll;
    window.addEventListener(REQUESTED_EVENT, requested);
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener(REQUESTED_EVENT, requested);
      pollNow.current = () => {};
    };
  }, [status, orgId, refresh]);

  const dismiss = useCallback((id) => {
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }, []);

  /* Throws on refusal so the notice can show why. The repository is marked queued here,
     so finishing again is announced again. */
  const retry = useCallback(
    async (repo) => {
      await api.post(`/organizations/${orgId}/repositories/${repo.id}/retry-index`);
      if (seen.current) seen.current.set(repo.id, "not_indexed");
      setNotices((current) => current.filter((notice) => notice.repo.id !== repo.id));
      pollNow.current();
    },
    [orgId],
  );

  if (!notices.length) return null;
  return (
    <div
      aria-live="polite"
      style={{
        position: "fixed",
        top: "16px",
        right: "16px",
        zIndex: 1000,
        display: "grid",
        gap: "10px",
        width: "min(360px, calc(100vw - 32px))",
      }}
    >
      {notices.map((notice) => (
        <Notice
          key={notice.id}
          repo={notice.repo}
          canRetry={canRetry}
          onRetry={retry}
          onDismiss={() => dismiss(notice.id)}
        />
      ))}
    </div>
  );
}

/* Success closes itself; a failure stays, because it is asking for something. */
function Notice({ repo, canRetry, onRetry, onDismiss }) {
  const failed = repo.indexStatus === "failed";
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    if (failed) return undefined;
    const timer = setTimeout(onDismiss, SUCCESS_MS);
    return () => clearTimeout(timer);
    // Bound once per notice: `onDismiss` is a new function each render but always
    // dismisses this same notice.
  }, [failed]);

  async function retry() {
    if (busy) return;
    setBusy(true);
    setFailure("");
    try {
      await onRetry(repo);
    } catch (error) {
      setFailure(error?.message || "Could not retry. Try again.");
      setBusy(false);
    }
  }

  return (
    <div
      role={failed ? "alert" : "status"}
      style={{
        ...panel,
        background: "var(--surface-elevated)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.28)",
        padding: "14px 16px",
        display: "grid",
        gridTemplateColumns: "auto minmax(0,1fr) auto",
        gap: "12px",
        alignItems: "start",
        animation: "cn-fade-up 320ms var(--ease-out) both",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: "8px",
          height: "8px",
          marginTop: "6px",
          borderRadius: "999rem",
          background: failed ? "var(--danger)" : "var(--success)",
        }}
      />
      <div style={{ minWidth: 0 }}>
        <strong style={{ display: "block", font: "var(--text-label)", color: "var(--text-primary)" }}>
          {failed ? "Indexing failed" : "Indexing complete"}
        </strong>
        <p style={{ font: "var(--text-caption)", color: "var(--text-muted)", margin: "4px 0 0", overflowWrap: "anywhere" }}>
          {failed
            ? `${repo.name}: ${indexErrorText(repo)}`
            : `${repo.name} is indexed and ready for reviews.`}
        </p>
        {failed && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginTop: "10px" }}>
            {canRetry ? (
              <button
                type="button"
                disabled={busy}
                aria-busy={busy}
                onClick={retry}
                style={{
                  font: "var(--text-caption)",
                  color: "var(--btn-primary-ink)",
                  background: "var(--btn-primary-bg)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "5px 12px",
                  cursor: busy ? "default" : "pointer",
                }}
              >
                {busy ? "Retrying..." : "Retry"}
              </button>
            ) : (
              <span style={{ font: "var(--text-caption)", color: "var(--text-disabled)" }}>
                Ask an admin to retry.
              </span>
            )}
            {canRetry && repo.indexError === "installation_unavailable" && (
              <Link to="/settings/providers" style={{ font: "var(--text-caption)", color: "var(--text-primary)" }}>
                Open Settings
              </Link>
            )}
          </div>
        )}
        {failure && (
          <p style={{ font: "var(--text-caption)", color: "var(--danger)", margin: "6px 0 0" }}>{failure}</p>
        )}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        style={{
          font: "var(--text-body-14)",
          lineHeight: 1,
          color: "var(--text-muted)",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: "2px 4px",
        }}
      >
        ×
      </button>
    </div>
  );
}
